import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { fetchApi } from '@/utils/api/customAxios';
import { getLegacyApprovalTranslationUrl } from '@/constant/approval';
import {
  isTranslatedAtom,
  isTranslatingAtom,
  translationResultAtom,
  type ApprovalTranslationResult,
} from '@/components/Approval/ApprovalView/atoms';

/**
 * 품의 본문 AI 번역 실행.
 *
 * 트리거 정책:
 *  - 사용자 액션 시점(메뉴에서 언어 선택 직후) 에 `execute(from, to)` 를 호출.
 *  - from/to atom 변경을 watch 하는 useEffect 자동 트리거는 두지 않는다 —
 *    atomWithLocalStorage 가 이전 세션 값을 복원하는 케이스에서, 사용자가 직전에
 *    "취소" 로 끈 상태(isTranslated=false) 인데도 새로고침 시 atom 값만으로 자동 재호출
 *    되는 버그를 피하기 위함.
 *
 * 동작 (`execute`):
 *  - documentId 없거나 from/to 비었거나 from===to 면 no-op.
 *  - `POST /api/v1/legacy-approval/{documentId}/translation?sourceLanguage=&targetLanguage=`
 *  - 응답 도착 시 `translationResultAtom` + `isTranslatedAtom=true` 설정.
 *
 * 동시 요청 안전성:
 *  - (docId, from, to) 키 기반 dedupe 는 두지 않는다. 사용자가 메뉴에서 같은 언어를
 *    다시 선택한 건 "취소 후 다시 켜고 싶다" 라는 명시적 의도이므로 막으면 안 됨.
 *  - 대신 in-flight 요청은 새 execute 호출 시 `cancelled` 플래그로 응답 적용을 막아
 *    레이스 컨디션을 차단 (마지막 호출의 결과만 atom 에 반영).
 *
 * 취소 동작은 본 훅 밖에서 `isTranslatedAtom=false` 만 세팅 — 결과 캐시는 유지되고,
 * from/to 도 유지 (사용자가 다시 같은 언어로 켜고 싶을 수도 있음).
 */
const useTranslateApproval = (documentId: string | undefined) => {
  const setResult = useSetAtom(translationResultAtom);
  const setIsTranslated = useSetAtom(isTranslatedAtom);
  const setIsTranslating = useSetAtom(isTranslatingAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const cancelInFlightRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      cancelInFlightRef.current?.();
    },
    [],
  );

  const execute = useCallback(
    (from: string | null, to: string | null) => {
      if (!documentId) return;
      if (!from || !to) return;
      if (from === to) return;

      cancelInFlightRef.current?.();
      let cancelled = false;
      cancelInFlightRef.current = () => {
        cancelled = true;
      };

      setIsLoading(true);
      // 응답 전에 띠를 "AI 번역중..." 으로 즉시 노출 (전역 atom).
      setIsTranslating(true);
      setError(null);

      const url = `${getLegacyApprovalTranslationUrl(documentId)}?sourceLanguage=${encodeURIComponent(from)}&targetLanguage=${encodeURIComponent(to)}`;

      fetchApi<{
        data: Omit<ApprovalTranslationResult, 'documentId' | 'targetLanguage'>;
      }>({
        method: 'POST',
        url,
      })
        .then((res) => {
          if (cancelled) return;
          // documentId / targetLanguage 는 요청 측에서 명시 주입 — API 응답이 본 필드를
          // 안 돌려줘도 (a) ApprovalView 의 stale 가드, (b) 상단 띠의 언어 표시 모두
          // 실제 적용된 번역과 정합 유지.
          setResult({
            ...res.data,
            documentId,
            targetLanguage: to,
          });
          // 같은 tick 에서 함께 갱신 → 띠가 "번역중" → "번역됨" 으로 깜빡임 없이 전환.
          setIsTranslated(true);
          setIsTranslating(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err);
          setIsTranslating(false);
        })
        .finally(() => {
          if (cancelled) return;
          setIsLoading(false);
        });
    },
    [documentId, setResult, setIsTranslated, setIsTranslating],
  );

  /**
   * 진행 중인 번역을 중단. in-flight 응답 적용을 막고(`cancelled`) 번역중 표시를 끈다.
   * isTranslated 는 건드리지 않음 — 원본 복귀 여부는 호출처(띠의 취소)에서 결정.
   */
  const cancel = useCallback(() => {
    cancelInFlightRef.current?.();
    cancelInFlightRef.current = null;
    setIsLoading(false);
    setIsTranslating(false);
  }, [setIsTranslating]);

  return { execute, cancel, isLoading, error };
};

export default useTranslateApproval;
